/**
 * React hooks for PostHog feature flags
 */

import { useState, useEffect, useMemo } from 'react';
import { usePosthog } from './usePosthog';
import { featureFlagManager } from '../services/posthog';

/**
 * Hook to check if a feature flag is enabled
 * Uses global feature flag manager to prevent memory leaks
 */
export const useFeatureFlag = (flagKey, defaultValue = false) => {
  const posthogService = usePosthog();

  // Get initial flag value immediately using the faster direct service call
  const initialValue = useMemo(
    () => posthogService.isFeatureEnabled(flagKey, defaultValue),
    [posthogService, flagKey, defaultValue],
  );

  const [isEnabled, setIsEnabled] = useState(initialValue);

  useEffect(() => {
    // Initialize the manager with PostHog service for subscriptions
    featureFlagManager.initialize(posthogService);

    // Get current flag value in case it changed since initial render
    const currentValue = featureFlagManager.getFeatureFlag(flagKey, defaultValue);
    setIsEnabled(currentValue);

    // Subscribe to flag updates
    const unsubscribe = featureFlagManager.subscribe(flagKey, (enabled) => {
      setIsEnabled(enabled);
    });

    // Cleanup subscription on unmount or dependency change
    return () => {
      unsubscribe();
    };
  }, [flagKey, defaultValue, posthogService]);

  return isEnabled;
};

/**
 * Hook to get feature flag with payload
 * Uses global feature flag manager to prevent memory leaks
 */
export const useFeatureFlagWithPayload = (flagKey, defaultValue = false) => {
  const posthogService = usePosthog();

  // Get initial values immediately to avoid flash of default
  const initialState = useMemo(
    () => ({
      enabled: posthogService.isFeatureEnabled(flagKey, defaultValue),
      payload: posthogService.getFeatureFlagPayload(flagKey) || null,
    }),
    [posthogService, flagKey, defaultValue],
  );

  const [state, setState] = useState(initialState);

  useEffect(() => {
    featureFlagManager.initialize(posthogService);

    // Sync current value in case it changed since initial render
    const currentEnabled = featureFlagManager.getFeatureFlag(flagKey, defaultValue);
    const currentPayload = featureFlagManager.getFeatureFlagPayload(flagKey) || null;
    setState({ enabled: currentEnabled, payload: currentPayload });

    const unsubscribe = featureFlagManager.subscribe(flagKey, (enabled, payload) => {
      setState({ enabled, payload: payload || null });
    });

    return () => {
      unsubscribe();
    };
  }, [flagKey, defaultValue, posthogService]);

  return state;
};
